import { InputFormulaTipTap } from '@vrplatform/components';
import { useInfiniteQuery, useTeam } from '@vrplatform/data';
import type { SelectItem } from '@vrplatform/components';
import { type Maybe, expression, toTitleCase } from '@vrplatform/utils';
import type { account_bool_exp } from '@vrplatform/graphql';
import { whereAccounts } from '@vrplatform/ui-common';
import { useMemo, useState } from 'react';

type Props = {
  formulaId: string | undefined;
  formattedFormula: string;
  onFormulaChange: (value: string) => void;
  error: boolean;
  setError: (value: boolean) => void;
  viewOnly?: boolean;
  disabled?: boolean;
  loading?: boolean;
  minHeight?: number;
  default_variables?: (
    | 'rate'
    | 'reservation.nights'
    | 'reservation.proRataNights'
    | 'reservation.guests'
    | 'reservation.revenue.owners'
    | 'reservation.revenue.manager'
  )[];
  customOptions?: SelectItem[];
};

type DefaultVariable = NonNullable<Props['default_variables']>[number];

export const GL_DEFAULT_FORMULA_FIELDS: SelectItem<DefaultVariable>[] = [
  {
    value: 'rate',
    label: 'Rate',
    description: 'The commission rate',
    group: 'Variables',
  },
  {
    value: 'reservation.revenue.owners',
    label: 'Net Revenue (owners)',
    group: 'Fields',
  },
  {
    value: 'reservation.revenue.manager',
    label: 'Net Revenue (manager)',
    group: 'Fields',
  },
  {
    value: 'reservation.proRataNights',
    label: 'Nights (statement period)',
    group: 'Fields',
  },
  {
    value: 'reservation.nights',
    label: 'Nights (reservation total)',
    group: 'Fields',
  },
  {
    value: 'reservation.guests',
    label: 'Reservation Guests',
    group: 'Fields',
  },
];

const Input = ({
  formattedFormula,
  onFormulaChange,
  error,
  setError,
  viewOnly,
  disabled,
  minHeight = 200,
  formulaId,
  default_variables,
  loading: loading2,
  customOptions,
}: Props) => {
  const [loading1, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [{ id: teamId }] = useTeam();

  const loading = Boolean(loading1 || loading2);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const variables = useMemo(() => {
    return GL_DEFAULT_FORMULA_FIELDS?.filter((x) =>
      default_variables?.includes(x.value)
    );
  }, [...(default_variables || [])]);

  const queryData = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      const where: account_bool_exp = {
        ...whereAccounts({
          search: args.search,
          tenantId: args.teamId,
        }),
        type: { _neq: 'nonPosting' },
      };

      const vars: SelectItem[] = args.variables.filter((x) =>
        search ? x.label.toLowerCase().includes(search.toLowerCase()) : true
      );

      const customOptions = (args.customOptions || []).filter((x) =>
        search ? x.label.toLowerCase().includes(search.toLowerCase()) : true
      );

      const accountAggregate =
        q.accountAggregate({ where }).aggregate?.count() || 0;

      const accounts = q
        .accounts({
          where,
          limit,
          offset,
          order_by: [
            { uniqueRef: 'asc_nulls_last' },
            { title: 'asc_nulls_last' },
          ],
        })
        .map<SelectItem>((account) => ({
          value: `acc.${account.id}`,
          label: account.title || 'No name',
          description:
            toTitleCase(account.category?.classification) || undefined,
          group: 'Accounts',
        }));

      // only show vars and custom amounts on first page
      const extras = offset === 0 ? [...vars, ...customOptions] : [];

      const list = [...extras, ...accounts];

      const aggregate = accountAggregate + extras.length;

      return {
        aggregate,
        list,
      };
    },
    {
      variables: {
        search: search.trim(),
        variables,
        teamId,
        formulaId,
        customOptions,
      },
    }
  );

  return (
    <InputFormulaTipTap
      initialValue={formattedFormula || ''}
      loading={loading}
      placeholder={'Formula'}
      minHeight={viewOnly ? undefined : minHeight}
      queryData={{ ...queryData, setSearch }}
      disabled={disabled}
      viewOnly={viewOnly}
      error={!!error}
      onChange={async (newValue) => {
        setError?.(false);
        let formula: Maybe<string>;

        // validate formula before saving
        try {
          if (!loading) {
            if (newValue?.trim() !== formattedFormula) {
              // validate formula
              const tree = expression.toTree(newValue || '');
              if (!tree) throw new Error('Invalid formula');

              formula = newValue;
            }

            setError?.(false);
          }
        } catch (error: any) {
          console.error(error);
          setError?.(true);
        }

        setLoading(false);

        // only save if formula is validated
        if (formula !== undefined) {
          onFormulaChange(formula || '');
        }
      }}
    />
  );
};

const Display = ({
  formattedFormula,
  type,
}: {
  formattedFormula: string;
  type: 'disabled' | 'viewOnly';
}) => (
  <Input
    error={false}
    formulaId={undefined}
    formattedFormula={formattedFormula}
    setError={() => {}}
    onFormulaChange={() => {}}
    viewOnly={type === 'viewOnly'}
    disabled={type === 'disabled'}
  />
);

export const FormulaGL = {
  Input,
  Display,
};
